<template>
  <div class="d-flex align-items-center justify-content-between">

    <div class="d-flex align-items-center">
      <h5 class="fw-bold mb-0">Elementi: </h5>
      <div class="d-flex flex-column p-2">
        <Render
          v-if="showRenderComponent"
          :data="data"
        />
      </div>
    </div>

    <template v-if="$props.isAbleToProject">
      <div>
        <button class="btn btn-violet text-uppercase position-relative" @click="showModal = true">
          <img class="me-2" src="@/assets/icons/upload.svg" alt="upload">
          carica file
          <span class="position-absolute fs-6 top-0 start-0 translate-middle badge rounded-pill bg-white text-violet border-violet shadow">
            P {{ numberElementsUploaded }}/{{ $props.elements.length }}
          </span>
          <template v-if="(modelUploaded + modelToBeUpload) !== 0">
            <span class="position-absolute fs-6 top-0 translate-middle badge rounded-pill bg-white text-violet border-violet shadow">
              M {{ modelUploaded }}/{{ modelUploaded + modelToBeUpload }}
            </span>
          </template>
        </button>
      </div>
    </template>

  </div>

  <Modal v-if="showModal"
         :size="'xl'"
         :modal_id="'project_upload'"
         :title="'caricamento file'"
         :subtitle="null"
         :decline_label="null"
         :lightLayout="false"
         @hidden="closeModal">
    <Upload
      :elements="data"
      :has-uploaded-some-models="hasUploadedSomeModels"
      :show-projects-title="(numberElementsUploaded !== $props.elements.length)"
      :show-models-title="(modelUploaded !== (modelUploaded + modelToBeUpload))"
      @removeBridge="removeBridge"
      @createBridge="createBridge"
      @asset="addAsset"
      @assetsIdsRemoved="removeAssets"
      @reset="removeSelection"
      @allFilesUploaded="readAllFilesUploaded"
      @clear-models-selected="clearModelSelected"
      @sync-models="syncModels"

      @force-models-upload="forceModelsHandle"
      @select="select"
      @remove-model="removeModel"
      @add-model="$emit('add-model', $event)"
    />
  </Modal>

</template>

<script>
import { computed, onMounted, reactive, ref, toRaw, watch, provide } from 'vue';

import Upload from "./Upload";
import Modal from '@/components/Modal/Standard';
import Render from '@/components/PrescriptionRows/Render';

import { updateBridge } from '@/use/repositories/prescriptionRows/updateBridge';
import { groupedByBridgesAndTooth } from '@/use/repositories/prescriptionRows/groupedByBridgesAndTooth';

import _find from "lodash/find";
import _head from "lodash/head";
import _camelCase from "lodash/camelCase";
export default {
  name: "Elements",
  props: [
    'elements',
    'models',
    'prescriptionMaterial',
    'prescriptionColorId',
    'defaultMaterials',
    'isAbleToProject',
    'atLeastOneFileUploaded',
  ],
  emits: [
    'remove-model',
    'add-model',
    'allFilesIsUploaded',
    'clear-models-selected',
    'sync-models',
    'remove-assets-models',
    'all-models-is-uploaded',
    'force-models-upload',
    'update:atLeastOneFileUploaded',
  ],
  components: {
    Upload,
    Modal,
    Render,
  },
  setup(props, {emit}) {
    const modelTypes = ['arch', 'gum', 'stump', 'custom-pin', 'gig', 'bracket', 'bar', 'base-arch'];
    const showModal = ref(false);
    const showRenderComponent = ref(false);

    const data = reactive({
      bridges: [],
      tooth: [],
      models: props.models,
      prescription_material: props.prescriptionMaterial,
      prescription_color_id: props.prescriptionColorId,
      default_materials: props.defaultMaterials ?? [],
    });

    const select = (itemId) => {
      const teeth = _find(data.bridges, bridge => _find(bridge.tooth, el => el.id == itemId)).tooth;
      const tooth = _find(teeth, tooth => tooth.id == itemId);
      tooth.selected = !tooth.selected;
    }

    const countAssets = (condition) => {
      return modelTypes.reduce((counter, modelType) => {
        const elements = props.models[_camelCase(modelType)]?.elements || [];
        return counter +  elements.filter(condition).length;
      }, 0);
    };

    const modelToBeUpload = computed(() => countAssets(el => el.assets.length === 0));

    const modelUploaded = computed(() => countAssets(el => el.assets.length > 0));

    watch(() => modelUploaded.value, () => {
      emit('all-models-is-uploaded', (modelUploaded.value === (modelUploaded.value + modelToBeUpload.value)));
    }, { immediate: true });

    const syncPrescriptionRows = async() => {
      // Hide render component
      showRenderComponent.value = false;

      // Simulate data processing
      const { bridges, tooth } = await new Promise(resolve => resolve(groupedByBridgesAndTooth(toRaw(props.elements))));
      data.bridges = bridges;
      data.tooth = tooth;

      // Show render component
      showRenderComponent.value = true;
    }

    const numberElementsUploaded = computed(() => {
      let count = 0;

      data.bridges.forEach((bridge) => {
        bridge.tooth.forEach((teeth) => {
          if (teeth.assets && teeth.assets.length > 0) {
            count++;
          }
        });
      });

      data.tooth.forEach((teeth) => {
        if (teeth.assets && teeth.assets.length > 0) {
          count++;
        }
      });

      return count;
    });

    const hasUploadedModelAssets = computed(() => {
      if (!props.models.must_upload) return false;
      for (const categoryKey in props.models) {
        if (!props.models[categoryKey]?.elements) continue;
        for (const element of props.models[categoryKey].elements) {
          if (Array.isArray(element.assets) && element.assets.length) return true;
        }
      }
      return false;
    });

    const hasAtLeastOneFileUploaded = computed(() =>
      hasUploadedModelAssets.value || numberElementsUploaded.value >= 1
    );

    const removeBridge = async (ids) => {
      props.elements.forEach((el) => {
        if (ids.includes(el.id)) {
          el.bridge = null;
        }
      });

      let bridge = {
        ['null'] : ids
      };

      syncBridge(bridge);
    }

    const createBridge = async (ids) => {

      let bridgeId = null;
      props.elements.forEach((el) => {
        if (ids.includes(el.id)) {
          // Not exist bridge.
          if (data.bridges.length === 0) {
            // Set the default index.
            el.bridge = 0;
            bridgeId = el.bridge;
          } else {
            // Set the last index bridge.
            el.bridge = data.bridges.length-1 !== -1 ? data.bridges.length : data.bridges.length-1;
            bridgeId = el.bridge;
          }
        }
      });

      let bridge = {
        [bridgeId]: ids
      };

      syncBridge(bridge);
    }

    const forceModelsHandle = (value) => {
      emit('force-models-upload', value);
    }

    const syncBridge = (data) => {
      updateBridge(data).then(async () => {
        // Sync data.
        syncPrescriptionRows();

      }).catch((error) => {
        console.log(error);
        alert("Non è stato possibile eseguire l'azione richiesta.");

      }).finally(async () => {
        await deselectedAll();
      });
    }

    const hasUploadedSomeModels = computed(
      () => Object.values(props.models).some(
        model => Array.isArray(model.elements) &&
          model.elements.some(
            item => Array.isArray(item.assets) && item.assets.length
          )
      )
    );

    const addAsset = async (obj) => {
      props.elements.forEach((el) => {
        if ('ids' in obj && obj.ids.includes(el.id)) {
          el.assets.push(obj.asset);
        }
      });

      // Sync data.
      syncPrescriptionRows();

    }

    const removeAssets = async (ids) => {
      // TODO check if assets model's ids.

      props.elements.forEach((el) => {
        let prescriptionRow =  el;
        if (prescriptionRow.assets.length > 0) {
          if (ids.includes(prescriptionRow.id)) {
            prescriptionRow.assets = [];
          }
        }
      });

      // Sync data.
      syncPrescriptionRows();

      // sync model.
      emit('remove-assets-models', ids);
    }

    const removeSelection = () => {
      console.log("RESET");
      deselectedAll();
    }

    const deselectedAll = () => {
      props.elements.forEach((el) => {
        el.selected = false;
      });
    }

    // Read emits.
    const closeModal = (value) => showModal.value = value;
    const readAllFilesUploaded = (value) => {
      emit('allFilesIsUploaded', value);
    }

    const clearModelSelected = () => emit("clear-models-selected");
    const syncModels = (obj) => emit("sync-models", obj);

    const removeModel = (model) => {
      const result = _head(model).replace(/\d+$/, '');

      emit("remove-model", result);
      emit('all-models-is-uploaded', (modelUploaded.value === (modelUploaded.value + modelToBeUpload.value)));
    }

    watch(hasAtLeastOneFileUploaded, (val) => {
      emit('update:atLeastOneFileUploaded', val);
    });

    onMounted(async () => {
      await syncPrescriptionRows();
      emit('allFilesIsUploaded', (numberElementsUploaded.value === props.elements.length));
    });

    provide('modelTypes', modelTypes);

    return {
      showModal,
      closeModal,
      data,
      removeBridge,
      createBridge,
      addAsset,
      removeAssets,
      removeSelection,
      readAllFilesUploaded,
      numberElementsUploaded,
      clearModelSelected,
      syncModels,
      modelToBeUpload,
      modelUploaded,
      forceModelsHandle,
      hasUploadedSomeModels,
      select,
      showRenderComponent,
      removeModel,
      hasUploadedModelAssets,
    }
  }
}
</script>
